import {
  Flex,
  Menu,
  MenuButton,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DateTimeFormatOptions } from 'luxon';
import React, { useEffect, useMemo } from 'react';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  DEFAULT_SHORT_LOCALE_OPTIONS,
  Timestamp,
} from 'packages/common-frontend/components/Timestamp';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { GetCreditAgreementDocumentsMetadataQueryGQLType } from '~/core/routes/settings/implementations/implementations.gql.generated';
import { RenameCreditAgreementModal } from '~/core/routes/settings/implementations/layout/CreditAgreementHistory/RenameCreditAgreementModal';

type TableData = {
  id: string;
  timestamp: number;
  version: string;
  displayName: string;
};

const TIMESTAMP_OPTIONS: DateTimeFormatOptions = {
  ...DEFAULT_SHORT_LOCALE_OPTIONS,
  year: 'numeric',
};

const columnHelper = createColumnHelper<TableData>();

const RenderMenu = ({ row }: CellContext<TableData, unknown>) => {
  const menuControls = useDisclosure();

  return (
    <Flex justify="right" overflow="hidden">
      <Menu {...menuControls} computePositionOnMount strategy="fixed">
        <MenuButton
          as={IconButtonV2}
          variant="secondary-muted"
          colorScheme="gray"
          isActive={menuControls.isOpen}
          aria-label="Facility options"
          icon={<Icon icon="ellipsis-vertical" />}
          tooltipEnabled={false}
        />
        <MenuList zIndex="dropdown">
          <RenameCreditAgreementModal
            creditAgreementId={row.original.id}
            existingDisplayName={row.original.displayName}
          />
        </MenuList>
      </Menu>
    </Flex>
  );
};

const COLUMN_DEFS = [
  columnHelper.accessor('timestamp', {
    header: 'Date',
    cell: function DateCell({ getValue }) {
      return (
        <Timestamp
          timestamp={getValue()}
          variant="datetime"
          shortLocaleFormat={TIMESTAMP_OPTIONS}
        />
      );
    },
  }),

  columnHelper.accessor('version', {
    header: 'Version',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('displayName', {
    header: 'Name',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.display({
    id: 'menu',
    header: '',
    cell: RenderMenu,
  }),
];

type CreditAgreementHistoryTableProps = {
  creditAgreementDocumentsMetadata: GetCreditAgreementDocumentsMetadataQueryGQLType;
  fetchAndSetPreviousCreditAgreement: (id: string) => void;
};
export const CreditAgreementHistoryTable = ({
  creditAgreementDocumentsMetadata,
  fetchAndSetPreviousCreditAgreement,
}: CreditAgreementHistoryTableProps) => {
  const previousCreditAgreement = useCreditAgreementStore(
    (store) => store.previousCreditAgreement,
  );

  const tableData: TableData[] = useMemo(() => {
    return creditAgreementDocumentsMetadata.creditAgreementDocumentsMetadata.map(
      (item) => ({
        id: item.id,
        timestamp: item.effectiveFrom,
        version: item.version,
        displayName: item.displayName,
      }),
    );
  }, [creditAgreementDocumentsMetadata]);

  const table = useReactTable({
    data: tableData,
    columns: COLUMN_DEFS,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
  });

  useEffect(() => {
    if (previousCreditAgreement) {
      table.setRowSelection({
        [previousCreditAgreement.id]: true,
      });
    }
  }, [previousCreditAgreement, table]);

  return (
    <TableBETA
      table={table}
      onRowClick={(row) => fetchAndSetPreviousCreditAgreement(row.original.id)}
    />
  );
};
